import React from "react"

import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import { TierGauge } from "@/components/charts/TierGauge"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { PlayheadTimeSeriesLineChart } from "../components/PlayheadTimeSeriesLineChart"
import { getStat } from "../helpers/getStat"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"
import { formatTimeToMinSec } from "../mixins/WithLLMAudioRegionsMixin"
import {
  contourPoints,
  contourSeries,
  detectSpans,
  EXPRESSIVE_FILL,
  EXPRESSIVE_ST,
  FLAT_ST,
  GAP_FACTOR,
  medianBinWidth,
  MONOTONE_FILL,
  pitchTier,
} from "./pitchVariabilityValues"

const log = logger("PitchVariabilityMetric")

// Gauge scale; the neutral band tracks the Flat→Expressive tier boundaries so the
// shaded region always matches the tier badge.
const GAUGE_MAX_ST = 6
const NEUTRAL_LOW_ST = FLAT_ST
const NEUTRAL_HIGH_ST = EXPRESSIVE_ST

interface F0SummaryStat {
  min?: number
  max?: number
  mean?: number
}

export class PitchVariabilityMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "st"
  aggregate_units = "st"

  // Summary "slider": headline semitone-std value, tier badge, and the gauge.
  // This is the compact piece that sits beside the comments column.
  private renderSummary(semitoneStd: number): React.ReactNode {
    return (
      <TierGauge
        value={semitoneStd}
        tier={pitchTier(semitoneStd)}
        min={0}
        max={GAUGE_MAX_ST}
        band={{ low: NEUTRAL_LOW_ST, high: NEUTRAL_HIGH_ST }}
        unit="st"
        maxTickLabel={`${GAUGE_MAX_ST}+ st`}
        bandNote={`Shaded = neutral range (${NEUTRAL_LOW_ST}–${NEUTRAL_HIGH_ST} st).`}
      />
    )
  }

  // Pitch-over-time contour with monotone/expressive highlights. Null when there
  // aren't enough voiced frames to draw a line.
  private renderContour(mo: RunMetricOutput): React.ReactNode | null {
    const points = contourPoints(mo.subvalues_by_timestamp ?? [])
    if (points.length < 2) {
      return null
    }
    const series = contourSeries(points, medianBinWidth(points) * GAP_FACTOR)
    const spans = detectSpans(mo.subvalues_by_timestamp ?? [])
    const maxT = points[points.length - 1].end

    return (
      <div className="space-y-1">
        <div className="text-[10px] text-coval-text-muted">
          amber = monotone · green = expressive · click to seek
        </div>
        <PlayheadTimeSeriesLineChart
          data={series.map((p) => ({ t: p.t, value: p.st, hz: p.hz }))}
          maxX={maxT}
          yAxisLabel="st from mean"
          formatX={formatTimeToMinSec}
          formatY={(v) => `${v > 0 ? "+" : ""}${Math.round(v)}`}
          referenceLines={[0]}
          highlights={spans.map((s) => ({
            start: s.start,
            end: s.end,
            fill: s.kind === "monotone" ? MONOTONE_FILL : EXPRESSIVE_FILL,
          }))}
          formatTooltipValue={(p) => {
            const st =
              typeof p.value === "number"
                ? `${p.value >= 0 ? "+" : ""}${p.value.toFixed(1)} st`
                : "—"
            return typeof p.hz === "number"
              ? `${st} · ${Math.round(p.hz)} Hz`
              : st
          }}
          tooltipLabel="Pitch"
          label="Pitch contour"
          summary={`${spans.filter((s) => s.kind === "monotone").length} monotone and ${spans.filter((s) => s.kind !== "monotone").length} expressive stretches shaded.`}
          stopClickPropagation
          onPointClick={(t) =>
            simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, { time: t })
          }
        />
      </div>
    )
  }

  // Register context (mean pitch + range in Hz) plus the explainer note.
  private renderRegister(mo: RunMetricOutput): React.ReactNode {
    const summary = getStat<F0SummaryStat>(mo, "summary", "f0_hz")
    const meanHz = summary?.mean
    const minHz = summary?.min
    const maxHz = summary?.max
    const register: Array<{ label: string; value: string }> = [
      {
        label: "Mean Pitch",
        value: meanHz != null ? `${meanHz.toFixed(0)} Hz` : "--",
      },
      {
        label: "Pitch Range",
        value:
          minHz != null && maxHz != null
            ? `${minHz.toFixed(0)}–${maxHz.toFixed(0)} Hz`
            : "--",
      },
    ]

    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-3">
          {register.map((card) => (
            <div
              key={card.label}
              className="rounded-lg bg-surface-1 border border-coval-border p-3"
            >
              <div className="text-xs text-coval-text-muted mb-1">
                {card.label}
              </div>
              <div className="text-lg font-semibold font-mono">
                {card.value}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-coval-text-muted">
          Register is the voice&apos;s pitch level — context, not a quality
          signal. Amber bands mark monotone stretches, green marks expressive
          ones; click the contour to jump the audio.
        </p>
      </div>
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    try {
      const semitoneStd = mo.float_value
      if (semitoneStd == null) {
        return (
          <div className="text-center py-8 text-coval-text-muted">
            No pitch variability data available
          </div>
        )
      }
      return (
        <div className="space-y-4">
          {this.renderSummary(semitoneStd)}
          {this.renderContour(mo)}
          {this.renderRegister(mo)}
        </div>
      )
    } catch (error) {
      log.error(
        "Error rendering pitch variability deep dive",
        error instanceof Error ? error : undefined,
      )
      return <div className="text-red-500">Error displaying metric details</div>
    }
  }

  // Expanded MetricGrid layout: the summary slider sits beside the comments
  // column; the contour and register cards span the full panel width below.
  getDeepDiveSections(
    mo: RunMetricOutput,
  ): { label: string; content: React.ReactNode }[] | null {
    try {
      const semitoneStd = mo.float_value
      if (semitoneStd == null) {
        return null
      }
      return [
        {
          label: "Pitch contour",
          content: (
            <div className="space-y-4">
              {this.renderContour(mo)}
              {this.renderRegister(mo)}
            </div>
          ),
        },
        // No label: the metric card header already reads "Pitch Variability".
        { label: "", content: this.renderSummary(semitoneStd) },
      ]
    } catch {
      return null
    }
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      return detectSpans(mo.subvalues_by_timestamp ?? []).map((s, idx) => ({
        id: `pitch_${s.kind}:${idx}`,
        start: s.start,
        end: s.end,
        color: `rgba(${s.kind === "monotone" ? MONOTONE_FILL : EXPRESSIVE_FILL}, ${s.alpha.toFixed(2)})`,
        drag: false,
        resize: false,
      }))
    } catch (e) {
      log.debug("Pitch variability contour unavailable or could not parse", {
        error: e,
      })
      return []
    }
  }
}
